import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Wizard, Card, FormField, FormSelect, TextInput } from '@/components/ui';
import type { WizardStep } from '@/components/ui/Wizard';
import { adminApi } from '@/shared/api/adminApi';
import { toast } from '@/components/ui/Toast';

interface NewAgent { name: string; email: string; extension: string }

// Suggest sequential extension numbers (101, 102, …) for agents that have none.
export function withSuggestedExtensions(agents: NewAgent[]): NewAgent[] {
  let next = 101;
  const used = new Set(agents.map((a) => a.extension).filter(Boolean));
  return agents.map((a) => {
    if (a.extension) return a;
    while (used.has(String(next))) next++;
    const ext = String(next++);
    used.add(ext);
    return { ...a, extension: ext };
  });
}

const DAYS = ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת'];
const DEFAULT_HOURS = DAYS.map((_, i) => ({ day_of_week: i, open: '08:00', close: i === 5 ? '13:00' : '18:00', enabled: i < 6 }));

// Pure builder for the /admin/onboarding/complete payload (testable).
export function buildOnboardingPayload(data: Record<string, unknown>): Record<string, unknown> {
  const agents = withSuggestedExtensions(((data.agents as NewAgent[]) ?? []).filter((a) => a.name && a.email));
  const didNumber = (data.did_number as string)?.trim();
  const routeTarget = (data.route_target as string) || '';

  return {
    company: {
      name: data.company_name,
      timezone: data.company_tz ?? 'Asia/Jerusalem',
      locale: data.company_locale ?? 'he',
    },
    queues: [{ name: (data.queue_name as string) || 'כללי', ext: (data.queue_ext as string) || '200' }],
    business_hours: DEFAULT_HOURS,
    ...(agents.length ? { agents } : {}),
    ...(didNumber ? { dids: [{ number: didNumber, type: 'virtual' }] } : {}),
    ...(didNumber && routeTarget
      ? { routing: { did: didNumber, route_type: (data.route_type as string) || 'queue', route_target: routeTarget } }
      : {}),
  };
}

export function Onboarding() {
  const navigate = useNavigate();
  const [done, setDone] = useState(false);

  const steps: WizardStep[] = [
    {
      id: 'welcome',
      title: 'ברוכים הבאים',
      render: () => <p style={{ margin: 0 }}>בואו נגדיר במהירות את החשבון: פרטי חברה, צוות, תור שירות ושעות פעילות. כל שלב נשמר בסיום.</p>,
    },
    {
      id: 'company',
      title: 'פרטי חברה',
      render: (ctx) => (
        <div className="stack">
          <FormField label="שם החברה" required>
            <TextInput value={(ctx.data.company_name as string) ?? ''} onChange={(e) => ctx.setData({ company_name: e.target.value })} />
          </FormField>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--gap)' }}>
            <FormField label="אזור זמן">
              <FormSelect
                value={(ctx.data.company_tz as string) ?? 'Asia/Jerusalem'}
                onChange={(e) => ctx.setData({ company_tz: e.target.value })}
              >
                {['Asia/Jerusalem', 'Europe/London', 'America/New_York', 'UTC'].map((t) => <option key={t} value={t}>{t}</option>)}
              </FormSelect>
            </FormField>
            <FormField label="שפה">
              <FormSelect
                value={(ctx.data.company_locale as string) ?? 'he'}
                onChange={(e) => ctx.setData({ company_locale: e.target.value })}
              >
                <option value="he">עברית</option>
                <option value="en">English</option>
              </FormSelect>
            </FormField>
          </div>
        </div>
      ),
      validate: (d) => !!d.company_name || 'חובה למלא שם',
    },
    {
      id: 'team',
      title: 'צוות',
      render: (ctx) => <AgentsStep agents={(ctx.data.agents as NewAgent[]) ?? []} onChange={(agents) => ctx.setData({ agents })} />,
    },
    {
      id: 'queue',
      title: 'תור שירות',
      render: (ctx) => (
        <div className="stack">
          <p style={{ color: 'var(--muted)', margin: 0, fontSize: 'var(--t-sm)' }}>ניצור תור שירות ראשון. ניתן להוסיף נוספים אחר כך.</p>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 'var(--gap)' }}>
            <FormField label="שם התור"><TextInput value={(ctx.data.queue_name as string) ?? 'כללי'} onChange={(e) => ctx.setData({ queue_name: e.target.value })} /></FormField>
            <FormField label="שלוחה"><TextInput value={(ctx.data.queue_ext as string) ?? '200'} onChange={(e) => ctx.setData({ queue_ext: e.target.value })} dir="ltr" /></FormField>
          </div>
        </div>
      ),
    },
    {
      id: 'extensions',
      title: 'שלוחות SIP',
      render: (ctx) => {
        const agents = withSuggestedExtensions((ctx.data.agents as NewAgent[]) ?? []);
        return (
          <div className="stack">
            <p style={{ color: 'var(--muted)', margin: 0, fontSize: 'var(--t-sm)' }}>
              לכל נציג תיווצר שלוחת SIP שתאפשר לו להתחבר לסופטפון. ניתן לשנות את המספרים.
            </p>
            {agents.length === 0 ? (
              <p style={{ color: 'var(--muted)', fontSize: 'var(--t-sm)' }}>לא הוגדרו נציגים — אפשר להוסיף שלוחות מאוחר יותר.</p>
            ) : (
              <div style={{ display: 'grid', gap: 6 }}>
                {agents.map((a, i) => (
                  <div key={i} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 8, alignItems: 'center' }}>
                    <span>{a.name}</span>
                    <TextInput
                      value={a.extension}
                      dir="ltr"
                      onChange={(e) => {
                        const next = [...agents];
                        next[i] = { ...next[i], extension: e.target.value };
                        ctx.setData({ agents: next });
                      }}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      },
    },
    {
      id: 'did',
      title: 'מספר טלפון',
      render: (ctx) => (
        <div className="stack">
          <p style={{ color: 'var(--muted)', margin: 0, fontSize: 'var(--t-sm)' }}>
            הזן את מספר הטלפון (DID) של העסק. שיחות נכנסות אליו ינותבו לפי השלב הבא.
          </p>
          <FormField label="מספר טלפון">
            <TextInput value={(ctx.data.did_number as string) ?? ''} onChange={(e) => ctx.setData({ did_number: e.target.value })} dir="ltr" placeholder="073-2745593" />
          </FormField>
        </div>
      ),
    },
    {
      id: 'routing',
      title: 'ניתוב שיחות',
      render: (ctx) => {
        const agents = withSuggestedExtensions((ctx.data.agents as NewAgent[]) ?? []);
        const routeType = (ctx.data.route_type as string) ?? 'queue';
        return (
          <div className="stack">
            <p style={{ color: 'var(--muted)', margin: 0, fontSize: 'var(--t-sm)' }}>
              לאן תגיע שיחה נכנסת למספר {(ctx.data.did_number as string) || 'שהוגדר'}?
            </p>
            <FormField label="סוג יעד">
              <FormSelect value={routeType} onChange={(e) => ctx.setData({ route_type: e.target.value, route_target: '' })}>
                <option value="queue">תור שירות</option>
                <option value="ext">שלוחה</option>
              </FormSelect>
            </FormField>
            <FormField label="יעד">
              <FormSelect value={(ctx.data.route_target as string) ?? ''} onChange={(e) => ctx.setData({ route_target: e.target.value })}>
                <option value="">— בחר —</option>
                {routeType === 'queue' && (
                  <option value={(ctx.data.queue_ext as string) || '200'}>
                    {(ctx.data.queue_name as string) || 'כללי'} ({(ctx.data.queue_ext as string) || '200'})
                  </option>
                )}
                {routeType === 'ext' && agents.filter((a) => a.extension).map((a) => (
                  <option key={a.extension} value={a.extension}>{a.name} ({a.extension})</option>
                ))}
              </FormSelect>
            </FormField>
          </div>
        );
      },
    },
    {
      id: 'hours',
      title: 'שעות פעילות',
      render: () => <p style={{ color: 'var(--muted)' }}>ברירת מחדל: א׳-ה׳ 08:00-18:00, ו׳ 08:00-13:00, שבת סגור. ניתן לשנות במסך "שעות פעילות".</p>,
    },
    {
      id: 'review',
      title: 'סיום',
      render: (ctx) => (
        <Card>
          <div className="stack" style={{ gap: 6 }}>
            <Row k="חברה" v={(ctx.data.company_name as string) || '—'} />
            <Row k="אזור זמן" v={(ctx.data.company_tz as string) || 'Asia/Jerusalem'} />
            <Row k="נציגים להוספה" v={String(((ctx.data.agents as NewAgent[]) ?? []).length)} />
            <Row k="תור שירות" v={`${(ctx.data.queue_name as string) || 'כללי'} (${(ctx.data.queue_ext as string) || '200'})`} />
            <Row k="מספר טלפון" v={(ctx.data.did_number as string) || '—'} />
            <Row k="ניתוב" v={(ctx.data.route_target as string) ? `${(ctx.data.route_type as string) === 'ext' ? 'שלוחה' : 'תור'} ${ctx.data.route_target}` : '—'} />
            <Row k="שעות פעילות" v="ברירת מחדל" />
          </div>
        </Card>
      ),
    },
  ];

  async function finish(data: Record<string, unknown>) {
    const payload = buildOnboardingPayload(data);
    try {
      const res = await adminApi.completeOnboarding(payload);
      const created = (res as { data?: { created?: { agents: number; queues: number; hours: number; extensions?: number; dids?: number } } })?.data?.created;
      toast(`המערכת מוכנה! נוצרו ${created?.queues ?? 0} תורים, ${created?.agents ?? 0} נציגים, ${created?.extensions ?? 0} שלוחות.`, 'ok');
      setDone(true);
      setTimeout(() => navigate('/admin'), 1200);
    } catch {
      toast('שגיאה בהקמה', 'danger');
    }
  }

  if (done) {
    return (
      <div style={{ padding: 'var(--gap)', maxWidth: 800 }}>
        <Card><p style={{ margin: 0 }}>ההקמה הושלמה — מעביר אותך ללוח הבקרה…</p></Card>
      </div>
    );
  }

  return (
    <div style={{ padding: 'var(--gap)', maxWidth: 800 }}>
      <h2 style={{ margin: '0 0 var(--gap) 0' }}>הקמה ראשונית</h2>
      <Card>
        <Wizard steps={steps} onFinish={finish} />
      </Card>
    </div>
  );
}

function AgentsStep({ agents, onChange }: { agents: NewAgent[]; onChange: (a: NewAgent[]) => void }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [ext, setExt] = useState('');

  function add() {
    if (!name.trim() || !email.trim()) {
      toast('יש למלא שם ואימייל', 'danger');
      return;
    }
    onChange([...agents, { name: name.trim(), email: email.trim(), extension: ext.trim() }]);
    setName(''); setEmail(''); setExt('');
  }

  return (
    <div className="stack">
      <p style={{ color: 'var(--muted)', margin: 0, fontSize: 'var(--t-sm)' }}>הוסף נציגים (אופציונלי). יקבלו סיסמה זמנית — אפס אותה במסך נציגים.</p>
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 2fr 1fr auto', gap: 8, alignItems: 'end' }}>
        <FormField label="שם"><TextInput value={name} onChange={(e) => setName(e.target.value)} /></FormField>
        <FormField label="אימייל"><TextInput type="email" value={email} onChange={(e) => setEmail(e.target.value)} dir="ltr" /></FormField>
        <FormField label="שלוחה"><TextInput value={ext} onChange={(e) => setExt(e.target.value)} dir="ltr" /></FormField>
        <button type="button" className="btn" onClick={add}>הוסף</button>
      </div>
      {agents.length > 0 && (
        <div style={{ display: 'grid', gap: 4 }}>
          {agents.map((a, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 12px', background: 'var(--surface-2)', borderRadius: 'var(--r-2)' }}>
              <span>{a.name} <span dir="ltr" style={{ color: 'var(--muted)', fontSize: 'var(--t-xs)' }}>{a.email}{a.extension ? ` · ${a.extension}` : ''}</span></span>
              <button type="button" className="btn danger" onClick={() => onChange(agents.filter((_, j) => j !== i))}>הסר</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px solid var(--line)' }}>
      <span style={{ color: 'var(--muted)' }}>{k}</span>
      <strong>{v}</strong>
    </div>
  );
}
